import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/CreateAdmin.css";
import axios from "axios";
import { GridLoader } from "react-spinners";

function CreateProduction() {
  const navigate = useNavigate();

  const [availableGroupIds, setAvailableGroupIds] = useState([]);
  const [confirmationModal, setConfirmationModal] = useState(false);

  const [newUser, setNewUser] = useState({
    unitName: "",
    address: "",
  });

  const [fieldErrors, setFieldErrors] = useState({});

  const baseURL = sessionStorage.getItem("baseURL");

  /* -------------------- AUTH & LOAD -------------------- */

  useEffect(() => {
    if (sessionStorage.getItem("isLoggedIn") !== "true") {
      navigate("/");
    } else if (sessionStorage.getItem("isSuperAdmin") === "true") {
      navigate("/super-admin");
    } else if (sessionStorage.getItem("isAdmin") !== "true") {
      navigate("/dashboard");
    } else {
      fetchGroupIds();
    }
  }, [navigate]);

  const fetchGroupIds = async () => {
    try {
      document.getElementById("loading").hidden = false;
      const res = await axios.get(baseURL + "pu/getGroupIds");
      setAvailableGroupIds(res.data.map((u) => u.production_unit_name));
    } catch (err) {
      console.error("Error fetching group IDs", err);
      setAvailableGroupIds([]);
    } finally {
      document.getElementById("loading").hidden = true;
    }
  };

  /* -------------------- HANDLERS -------------------- */

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: false }));
    if (name === "unitName" && availableGroupIds.includes(value)) {
      setFieldErrors((prev) => ({
        ...prev,
        unitName: "Production Unit Name already exists",
      }));
    }
  };

  /* -------------------- VALIDATION -------------------- */

  const validateFields = () => {
    const errors = {};
    if (!newUser.unitName.trim()) errors.unitName = "Production Unit Name is required";
    if (!newUser.address.trim())  errors.address  = "Address is required";
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  function showToast(message, type = "success") {
    const toast = document.createElement("div");
    toast.innerText = message;
    const bg =
      type === "error"   ? "#C0392B" :
      type === "warning" ? "#e67e22" :
      "#1a6b3c";
    Object.assign(toast.style, {
      position: "fixed",
      top: "20px",
      right: "20px",
      background: bg,
      color: "#fff",
      padding: "12px 20px",
      borderRadius: "8px",
      boxShadow: "0 6px 20px rgba(0,0,0,0.18)",
      zIndex: 9999,
      opacity: "0",
      fontSize: "0.92rem",
      fontFamily: "'Source Sans 3', system-ui, sans-serif",
      fontWeight: "600",
      letterSpacing: "0.01em",
      transition: "opacity 0.3s ease",
    });
    document.body.appendChild(toast);
    requestAnimationFrame(() => (toast.style.opacity = "1"));
    setTimeout(() => {
      toast.style.opacity = "0";
      setTimeout(() => toast.remove(), 300);
    }, 3500);
  }

  /* -------------------- SUBMIT -------------------- */

  const addPUDetails = async () => {
    const formData = new FormData();
    formData.append("productionUnitName", newUser.unitName);
    formData.append("address", newUser.address);
    try {
      document.getElementById("loading").hidden = false;
      axios.post(baseURL + "pu/addPU", formData, {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
        }).then(() => {
        document.getElementById("loading").hidden = true;
        showToast("Production Unit Added Successfully");
        navigate("/users");
      });
    } catch (err) {
      console.error("Add Production Unit failed", err);
      showToast("Error adding Production Unit. Please try again.", "error");
    } finally {
      document.getElementById("loading").hidden = true;
    }
  };

  const handleAddProductionUnit = () => {
    if (!validateFields()) return;
    setConfirmationModal(true);
  };

  /* -------------------- UI -------------------- */

  return (
    <div className="container-fluid py-4">
      <div className="card admin-page">

        {/* ── Title ── */}
        <h2 className="text-center">Add Production Unit</h2>

        {/* ── Unit Name ── */}
        <div className="mb-3">
          <label htmlFor="unitName">Production Unit Name *</label>
          <input
            id="unitName"
            type="text"
            className={`form-control ${fieldErrors.unitName ? "is-invalid" : ""}`}
            value={newUser.unitName}
            name="unitName"
            placeholder="e.g. Unit A – Ahmedabad"
            onChange={handleInputChange}
            autoComplete="off"
          />
          {fieldErrors.unitName && (
            <div className="invalid-feedback">{fieldErrors.unitName}</div>
          )}
        </div>

        {/* ── Address ── */}
        <div className="mb-3">
          <label htmlFor="address">Address *</label>
          <textarea
            id="address"
            className={`form-control ${fieldErrors.address ? "is-invalid" : ""}`}
            name="address"
            value={newUser.address}
            placeholder="Enter full address"
            onChange={handleInputChange}
          />
          {fieldErrors.address && (
            <div className="invalid-feedback">{fieldErrors.address}</div>
          )}
        </div>

        {/* ── Actions ── */}
        <div className="text-center" style={{ display: "flex", justifyContent: "center", gap: "12px" }}>
          <button
            className="btn btn-secondary"
            onClick={() => navigate("/users")}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleAddProductionUnit}
          >
            Add Production Unit
          </button>
        </div>
      </div>

      {/* ── Loading ── */}
      <div className="loading" id="loading" hidden>
        <GridLoader color="var(--c-navy)" size={18} />
      </div>

      {/* ── Confirmation Modal ── */}
      {confirmationModal && (
        <div className="modal-overlay">
          <div className="modal-box" role="dialog" aria-modal="true">
            <h2 className="modal-title">Confirm Production Unit</h2>

            <p className="modal-message">
              Are you sure you want to add this production unit?
            </p>

            <p className="modal-note">
              <strong>Note:</strong> Once added, the production unit name cannot be changed.
            </p>

            <div className="modal-actions">
              <button
                className="btn btn-cancel"
                onClick={() => setConfirmationModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-confirm"
                onClick={addPUDetails}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreateProduction;